/**
 * Force Update Modal
 * Blocking modal that requires users to update the app
 * Cannot be dismissed - user must update or close the app
 */

import React from 'react';
import { Download, AlertTriangle, Wrench } from 'lucide-react';

interface ForceUpdateModalProps {
  isOpen: boolean;
  message: string;
  storeUrl: string;
  isMaintenanceMode?: boolean;
  onUpdate: () => void;
}

export const ForceUpdateModal: React.FC<ForceUpdateModalProps> = ({
  isOpen,
  message,
  storeUrl,
  isMaintenanceMode = false,
  onUpdate
}) => {
  if (!isOpen) return null;

  const lang = localStorage.getItem('language') || 'en';

  const translations = {
    en: {
      updateRequired: 'Update Required',
      maintenance: 'Maintenance',
      updateButton: 'Update Now',
      maintenanceNote: 'Please check back later'
    },
    es: {
      updateRequired: 'Actualización Requerida',
      maintenance: 'Mantenimiento',
      updateButton: 'Actualizar Ahora',
      maintenanceNote: 'Por favor regrese más tarde'
    }
  };

  const t = translations[lang as 'en' | 'es'] || translations.en;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '360px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: isMaintenanceMode ? '#fef3c7' : '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          {isMaintenanceMode ? (
            <Wrench size={40} color="#d97706" />
          ) : (
            <AlertTriangle size={40} color="#dc2626" />
          )}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1e293b',
          marginBottom: '16px'
        }}>
          {isMaintenanceMode ? t.maintenance : t.updateRequired}
        </h2>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#64748b',
          lineHeight: '1.6',
          marginBottom: '32px'
        }}>
          {message}
        </p>

        {/* Action */}
        {isMaintenanceMode ? (
          <p style={{
            fontSize: '14px',
            color: '#94a3b8',
            fontStyle: 'italic'
          }}>
            {t.maintenanceNote}
          </p>
        ) : (
          <button
            onClick={onUpdate}
            style={{
              width: '100%',
              backgroundColor: '#22c55e',
              color: 'white',
              padding: '16px 24px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
          >
            <Download size={22} />
            {t.updateButton}
          </button>
        )}
      </div>
    </div>
  );
};
